import React, {useEffect, useState, useContext, useMemo} from 'react';
import './style.scss';
import DataGrid from '../../components/DataGrid';
import financialsColumns from './FinancialsDatagridModel';
import {
  fetchFinancials,
  deleteFinancials,
  changeFinancialsbyId,
  createFinancials,
  changeFinancialsValidate
} from '../../services/financials';
import {maskInput, createOrUpdateDataFull, formatDateTime} from '../../utils/utils';
import {MASKS} from '../../utils/constants';
import {DataContext} from '../../utils/DataProvider';
import {useModuleNameContextUpdate} from '../../utils/Providers/ModuleNameProvider';
import financialModuleMap from './FinancialModuleMap';

const FinancialsGrid = ({match}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [update, setUpdate] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sort, setSort] = useState({field: 'id', order: 'desc'});

  const {state, stateFetchParams, stateMessage, filterParams} = useContext(DataContext);

  const updateModuleName = useModuleNameContextUpdate();
  const financialModuleName = financialModuleMap(match.params.slug);

  const headerConfig = useMemo(
    () => financialsColumns({costCenter: match.params.slug}, {user: state.user}, state),
    [match.params.slug, state]
  );

  const validateHeaderCallback = async (dataGridRows, field, checked) => {
    setIsLoading(true);

    try {
      const list = dataGridRows.map((item) => item.id);
      await changeFinancialsValidate(checked, list);
      setUpdate((prev) => !prev);
      return [];
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateModuleName(financialModuleName);
  }, [financialModuleName, updateModuleName]);

  useEffect(() => {
    setItemsPerPage(50);
    setCurrentPage(0);
  }, [match.params.slug]);

  useEffect(() => {
    const getContents = async () => {
      try {
        setIsLoading(true);

        const params = {
          ...stateFetchParams,
          ...filterParams,
          type: match.params.slug.toUpperCase(),
          page: currentPage,
          perPage: itemsPerPage,
          [`order[${sort.field}]`]: sort.order,
        };

        console.log('fetchFinancials params', params);

        const response = await fetchFinancials(params);

        setTotalItems(response?.data?.size || 0);
        setData(response?.data?.resultList || []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    getContents();
  }, [
    match.params.slug,
    currentPage,
    itemsPerPage,
    sort,
    stateFetchParams,
    filterParams,
    update
  ]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filterParams, stateFetchParams, match.params.slug]);

  const actions = [
    {
      title: 'Adicionar',
      action: 'add',
      icon: 'fas fa-plus',
    },
    {
      title: 'Editar',
      action: 'edit',
      icon: 'fas fa-pencil-alt',
    },
    {
      title: 'Excluir',
      action: 'remove',
      icon: 'fas fa-trash-alt',
      onClick: async (value) => {
        try {
          const res = await deleteFinancials(value.id);
          setUpdate((prev) => !prev);
          return res;
        } catch (error) {
          console.error(error);
          return error;
        }
      },
    },
    {
      title: 'Salvar',
      action: 'save',
      icon: 'fas fa-save',
      onClick: () => {},
    },
    {
      title: 'Cancelar',
      action: 'cancel',
      icon: 'fas fa-times',
    },
  ];

  return (
    <>
      {stateMessage}
      <DataGrid
        onChange={async (dataGridInputs) => {
          if (!dataGridInputs.date) {
            const date = formatDateTime(new Date());
            Object.assign(dataGridInputs, {date});
          }

          Object.assign(dataGridInputs, {
            status: false,
            type: match.params.slug.toUpperCase(),
          });

          const res = await createOrUpdateDataFull(
            dataGridInputs,
            state,
            createFinancials,
            changeFinancialsbyId
          );

          setUpdate((prev) => !prev);
          return res;
        }}
        header={headerConfig}
        data={data}
        keyProp="key"
        keyValueProp="valueKey"
        titleProp="name"
        editable=""
        actions={actions}
        totalItems={totalItems}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(0);
        }}
        sort={sort}
        onSortChange={setSort}
        validateCallback={validateHeaderCallback}
        accumulators={{
          'Total geral': {
            columns: ['value'],
            itemOperation: (acc, line, key) => (acc += line[key]),
            resultOperation: (acc) => maskInput({value: acc, type: MASKS.MONEY}),
          },
        }}
      />
      {isLoading && (
        <div className="overlay">
          <div className="spinner" />
        </div>
      )}
    </>
  );
};

export default FinancialsGrid;